import { useMutation } from '@tanstack/react-query';

import ExternalService from '../services/ExternalService/ExternalApiService';
import { type Rule } from '../services/types';

interface ApiResponse {
  success: boolean; // Indicates if the API call was successful
  errorMessage?: string; // Optional message from the server
  data?: Rule; // The actual data returned by the API
  error?: ErrorResponse; // Error details in case of failure
  status?: number;
  ok?: boolean;
}
interface ErrorResponse {
  apiPath: string;
  errorCode: string;
  errorMessage: string;
  errorTime: string;
  validationErrors: Record<string, string>;
}

/**
 * Sends a request to update a rule using the external API service.
 *
 * @async
 * @function postRule
 * @param {Rule} rule - The rule object containing the updated data.
 * @returns {Promise<Rule>} A promise that resolves to the updated rule data.
 */
const postRule = async (rule: Rule) => {
  const response = (await ExternalService.updateRule(rule)) as ApiResponse;

  return response;
};

/**
 * Custom React hook to update a rule using React Query's `useMutation`.
 *
 * @function useUpdateRule
 * @returns {import('@tanstack/react-query').UseMutationResult<Rule, unknown, Rule, unknown>} A mutation object that includes the mutate function, mutation status, and more.
 *
 */
const useUpdateRule = () =>
  useMutation({
    mutationFn: (rule: Rule) => postRule(rule), // Function to fetch data
  });

export default useUpdateRule;
